import { key } from '../scene/lighting.js';
import { histOn, pushHistory } from '../state/history.js';
import { el, fmt } from '../state/store.js';
import { renderLeft, renderRight } from '../ui/rails.js';

/* ═══════════════════════════════════════════════════════════
   UI BUILDERS
   ═══════════════════════════════════════════════════════════ */
/* Every panel is rebuilt from scratch on nearly every interaction (see this
   file's own note on captureFocus, and rail-right.js's "rebuilt wholesale,
   not diffed") — a <details> element defaults to open with no memory of
   its own, so without tracking this separately, collapsing a panel would
   pop back open the instant you touched any field anywhere in the dock.
   Keyed by title alone, not per object: collapsing "Transform" is a
   statement about how much of that group you want to see, not something
   tied to which specific object happens to be selected right now. */
/* A Map rather than a set of collapsed titles, because a panel now has three
   states to tell apart and a set can only carry two: open, closed, and NEVER
   TOUCHED — which is the only one that may fall back to the caller's default.
   With a set, a panel that defaults to closed could never be opened, since
   "not in the set" and "explicitly opened" looked identical and the default
   won on every rebuild. */
const panelState = new Map();

/**
 * @param {string} title  shown in the header, and the collapse-memory key
 *   unless opts.key says otherwise
 * @param {string} [badge]
 * @param {{key?:string, closed?:boolean}} [opts]
 *   `key` separates the memory of two panels that share a title — the Scene
 *   list is the same list in Design and in Motion but wants its own open
 *   state in each. `closed` is the state before anyone has touched it.
 */
export function panel(title, badge, opts = {}){
  const key = opts.key || title;
  const p = el('details','panel');
  p.open = panelState.has(key) ? panelState.get(key) : !opts.closed;
  p.addEventListener('toggle', ()=>{ panelState.set(key, p.open); });
  const hd = el('summary','panel__hd');
  const h3 = el('h3'); h3.appendChild(document.createTextNode(title));
  if(badge){ const u=el('u',null,badge); h3.appendChild(u); }
  hd.appendChild(h3);
  const bd = el('div','panel__bd');
  p.appendChild(hd); p.appendChild(bd);
  p.body = bd;
  return p;
}
/* Label above, control full width — for anything too wide to sit beside a label. */
export function stackRow(label, node){
  const f = el('div','v3');
  f.dataset.field = label;
  f.appendChild(el('label',null,label));
  f.appendChild(node);
  return f;
}
/* An <input type=number> REJECTS a comma outright, at the keystroke level, in
   every browser regardless of locale — that's the HTML spec (the value IDL
   attribute only ever recognises "." as the decimal point), not something an
   app can configure. The native spinner arrows are worth more than comma
   input, so that's the trade made here: a typed comma is simply rejected by
   the field, same as any other invalid character. */
export function decimalInput(i){
  i.type = 'number';
  return i;
}
/* Figma-style drag-to-scrub. Press on a field's label (or an axis letter) and
   drag sideways to change the number. Shift is coarse, Alt is fine. A pointer
   capture keeps the drag alive outside the element, and one history entry is
   pushed at the end rather than on every pixel of movement. */
function decimalsFor(step){
  const s = String(step);
  return s.indexOf('.') < 0 ? 0 : s.length - s.indexOf('.') - 1;
}
/* No FIELD shows more than a hundredth, whatever its step or its stored
   precision — a metre field finer than a centimetre reads as noise, not as
   care. Kept apart from decimalsFor itself, which still answers "how many
   decimals does this STEP imply" for the handful of callers rounding a
   value to commit it (numInput's own stepper wiring below) — that rounding
   has to stay at the step's own granularity, or a field stepped in .002s
   would round every click back to where it started. This constant is only
   for turning a number into the TEXT shown in a box. */
const DISPLAY_DP_MAX = 2;
const displayDp = step => Math.min(DISPLAY_DP_MAX, decimalsFor(step));
/* Many field handlers rebuild a whole rail — the recess ones re-fit and redraw
   the inspector on every change. That destroys the very element being dragged, so
   the scrub died after one pixel. Hold rebuilds until the drag ends, then run
   whichever were asked for, so scrubbing works on every numeric field rather than
   only those whose handlers happen to leave the DOM alone. */
export let scrubbing = false, railLPending = false, railRPending = false;
function flushRails(){
  if(railLPending){ railLPending = false; renderLeft(); }
  if(railRPending){ railRPending = false; renderRight(); }
}

/* Same problem as the scrub case, different trigger: several field handlers
   chain a rebuild into their own onChange so some other readout in the same
   panel stays current (Cap height, Extrusion, a recess's auto-fit dimensions,
   Text Area's Width). Rebuilding on every keystroke doesn't just cost focus —
   it's actively destructive for a number input specifically: `.value` on
   `type=number` never reflects an incomplete decimal ("2." reads back as
   plain "2", by spec — try it on any bare number input, no app involved), so
   even a rebuild that faithfully restores whatever `.value` says has already
   lost the trailing dot the user just typed. There is no DOM property that
   would give it back. The only fix is to not tear the node out from under an
   in-progress edit at all: hold rebuilds exactly like a scrub does, for as
   long as the field actually holds focus.

   There is deliberately NO timer here. An earlier version also cleared the
   flag 500ms after the last keystroke, which quietly reopened the very hole
   it was meant to close: pause mid-number with a trailing dot on screen —
   ordinary if you stop to think — and the rebuild fires anyway, the dot is
   dropped, and the following digits land against the truncated value, so
   "1.75" commits as 175. Focus is the only signal that honestly means "done
   with this field", so it is the only one used, and the pending rebuild runs
   on the way out. The cost is that a chained readout elsewhere in the panel
   updates when you leave the field rather than mid-word, which is the
   quieter behaviour anyway. */
const isTextEditable = n => !!n && (n.tagName==='TEXTAREA' ||
  (n.tagName==='INPUT' && (n.type==='number' || n.type==='text')) || n.isContentEditable);
export const isEditing = () => isTextEditable(document.activeElement);
/* The escape hatch that keeps the hold above from ever becoming a stall.
   Deferring is right for a field rebuilding itself mid-keystroke, but a
   STRUCTURAL change — selecting another object, undo, applying a mask — means
   the panel now has to show something else entirely, so the edit the deferral
   was protecting has nothing left to protect. Blurring ends it and lets the
   pending rebuild through immediately; without it the rail would sit stale
   until the user happened to click away. Called from renderRails(), which is
   what every structural path goes through — never from a single-rail
   renderRight(), which is exactly the per-keystroke case being protected. */
export function endFieldEdit(){
  const a = document.activeElement;
  if(isTextEditable(a)) a.blur();
}
/* Deferred to a task rather than run inline: during focusout, whether
   document.activeElement has already moved off the outgoing field is exactly
   the kind of detail browsers disagree on, and reading it too early would
   make flushRails re-defer against the field it is trying to release. */
document.addEventListener('focusout', e=>{
  if(isTextEditable(e.target)) setTimeout(flushRails, 0);
}, true);
/**
 * @param {'x'|'y'} axis - 'x' drags sideways (field labels); 'y' drags
 *   vertically (the up/down stepper below) with up meaning increase, which
 *   is why its delta is negated — screen Y grows downward.
 * @param {()=>void} [onClick] - fires on pointerup if the pointer never
 *   passed the moved-threshold, i.e. this was a click/tap, not a drag. The
 *   stepper's up/down halves use this for a single discrete step.
 */
function makeScrub(handle, get, set, step, axis='x', onClick){
  handle.classList.add('scrub');
  let on = false, p0 = 0, v0 = 0, moved = false;
  handle.addEventListener('pointerdown', e=>{
    if(e.button !== 0) return;
    /* A press that starts on a BUTTON inside the handle is that button's, not a
       scrub. The stopwatch lives inside this label (ui/timeline-view.js), and
       capturing the pointer here retargets everything that follows to the label
       — including the click — so the button would never hear its own press.
       Left alone it worked only by accident, through the label activation that
       the click handler below now cancels. */
    if(e.target.closest && e.target.closest('button')) return;
    on = true; moved = false; p0 = axis==='y' ? e.clientY : e.clientX; v0 = get();
    scrubbing = true;
    if(!isFinite(v0)) v0 = 0;
    try{ handle.setPointerCapture(e.pointerId); }catch(_){}
    e.preventDefault();
  });
  handle.addEventListener('pointermove', e=>{
    if(!on) return;
    const raw = (axis==='y' ? e.clientY : e.clientX) - p0;
    const d = axis==='y' ? -raw : raw;
    if(Math.abs(raw) > 2) moved = true;
    if(!moved) return;
    const mult = e.shiftKey ? 10 : (e.altKey ? .1 : 1);
    set(v0 + d * step * mult);
  });
  const stop = e=>{
    if(!on) return;
    on = false;
    scrubbing = false;
    flushRails();
    try{ handle.releasePointerCapture(e.pointerId); }catch(_){}
    if(moved) pushHistory();
    else if(onClick) onClick(e);
  };
  handle.addEventListener('pointerup', stop);
  handle.addEventListener('pointercancel', stop);
  /* A <label> FORWARDS ITS CLICK to the first labelable element inside it, and
     a <button> is labelable. The stopwatch (ui/timeline-view.js's stopwatch())
     is inserted as the label's firstChild, so every click on the label — and
     every scrub, since a drag ends in a click, retargeted to the label by the
     pointer capture above — was activating it. Dragging a value in Motion
     therefore turned that property's animation on, or off again if it was
     already on: reported as "any adjustment using the drag over text creates a
     keyframe", and it did.

     Cancelled only when the label ITSELF is the target. A real click on the
     stopwatch has the button as its target, is not label activation at all, and
     still reaches its own handler untouched. */
  handle.addEventListener('click', e=>{ if(e.target === handle) e.preventDefault(); });
}

/* Compact up/down stepper — replaces the browser's native number-input
   spinner, which this app hides globally (app.css) because it eats enough
   width to clip the value in a narrow field (worst in vec3Row's 3-column
   layout: "1.000" was rendering as "1,00(" with nothing left to show the
   rest). One vertical strip, split into an up half and a down half: click
   either half for one discrete step, or press-drag vertically anywhere on
   it to scrub continuously — same modifier keys (Shift coarse, Alt fine) as
   every other scrub handle here, so the behaviour stays consistent even
   though this one drags vertically instead of sideways. */
const CHEVRON_UP = '<svg viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 4.5 5 1.5l3.5 3"/></svg>';
const CHEVRON_DN = '<svg viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 1.5 5 4.5l3.5-3"/></svg>';
function addStepper(container, get, set, step){
  // NOT .stepper — already the topbar Calibrate/Design mode switch's class
  // (index.html), which this silently corrupted the layout of before it
  // got its own name; see the .numstep comment in app.css.
  const wrap = el('div','numstep');
  const up = el('div','numstep__up'); up.innerHTML = CHEVRON_UP;
  const dn = el('div','numstep__dn'); dn.innerHTML = CHEVRON_DN;
  wrap.appendChild(up); wrap.appendChild(dn);
  container.appendChild(wrap);
  // Which half a click landed on — read by the shared drag handle below
  // only when the pointer never moved, i.e. this turned out to be a click
  // rather than a scrub.
  let dir = 1;
  wrap.addEventListener('pointerdown', e=>{ dir = e.target.closest('.numstep__up') ? 1 : -1; });
  makeScrub(wrap, get, set, step, 'y', ()=>{ set(get() + dir*step); pushHistory(); });
  return wrap;
}

export function fieldRow(label, ctlNode){
  const f = el('div','f');
  f.dataset.field = label;
  const l = el('label',null,label);
  /* The label column is a fixed 78px and ellipsises, so a long name reads as
     "Right panel wi…" with no way to find out the rest. Carrying the full text
     as a tooltip costs nothing and makes every truncated label in the app
     recoverable — the real fix is still a shorter name, but this stops the
     truncation being a dead end. */
  l.title = label;
  const c = el('div','ctl'); c.appendChild(ctlNode);
  f.appendChild(l); f.appendChild(c);
  return f;
}
/* ONE editable number: the input, its rounding-on-blur behaviour and its
   precise stored value. Split out of numInput so a row can hold TWO of them —
   a screen is a width AND a height, and a corner screen is two of each, which
   is a shape you cannot express one row per number without the reader having
   to reassemble it.

   Fields carry the full stored value, but auto-fill and scaling produce long
   tails like 0.236250000. Show that rounded to the field's own precision while
   idle, reveal the full number when you click in to fine-tune, and re-round on
   blur \u2014 the way a DCC app does. Finer input than the step is preserved:
   whatever you type is stored, only the resting display is rounded. */
export function numCell(value, step, onChange, key){
  const dp = decimalsFor(step);
  const round = v => +(+v).toFixed(dp);
  const i = decimalInput(el('input','inp'));
  i.step = step;
  /* Named per CELL, not per row: restoreFocus looks a field up by data-field
     after a rebuild, and two cells in one row have to be tellable apart or
     typing into the second hands your focus to the first. */
  if(key) i.dataset.field = key;
  let full = isFinite(value) ? +value : NaN;        // best-known precise value
  i.value = isFinite(full) ? String(round(full)) : (value==null ? '' : value);
  i.addEventListener('focus',()=>{ if(isFinite(full)) i.value = String(full); });
  i.addEventListener('input',()=>{ const v=parseFloat(i.value); if(isFinite(v)){ full=v; onChange(v); } });
  i.addEventListener('blur', ()=>{ i.value = isFinite(full) ? String(round(full)) : ''; });
  histOn(i);
  /* Routes every external write (the stepper, numRow's own label-drag) through
     the same path typing does, so `full` never goes stale \u2014 it used to only
     track keystrokes, so a scrub-set value would get silently reverted the next
     time the field was focused (its focus handler restores `full`, unaware
     anything else had just changed the field). */
  i.setValue = v=>{ full = v; i.value = isFinite(full) ? String(round(full)) : ''; onChange(v); };
  i.getFull = ()=>full;
  return i;
}
function numInput(value, step, onChange, unit){
  const c = el('div','ctl');
  const i = numCell(value, step, onChange);
  c.appendChild(i);
  if(unit) c.appendChild(el('span','unit',unit));
  c.input = i;
  c.setValue = i.setValue;
  addStepper(c, i.getFull, v=>c.setValue(+(+v).toFixed(decimalsFor(step))), step);
  return c;
}
/* `key` names the ROW; `label` names it to the reader. They are the same thing
   almost everywhere, and separable for the case where they cannot be: two rows
   in one rail that mean different things but read identically. data-field is
   what restoreFocus looks a row up by after a rebuild, and it uses the FIRST
   match — so two rows sharing a label meant typing into the second one and
   having the rebuild hand your focus, and the half-typed text with it, to the
   first. A corner screen has exactly that: a pixel count per panel. */
export function numRow(label, value, step, onChange, unit, key){
  const f = el('div','f');
  f.dataset.field = key || label;
  const lab = el('label',null,label);
  f.appendChild(lab);
  const c = numInput(value, step, onChange, unit);
  f.appendChild(c);
  f.input = c.input;
  const dp = decimalsFor(step);
  /* Quantise to the field's own precision and store THAT, so the number you
     see is the number that is kept — displaying a rounded value while storing
     the raw one would make the field jump the moment the inspector redraws. */
  makeScrub(lab, ()=>parseFloat(c.input.value), v=>c.setValue(+v.toFixed(dp)), step);
  return f;
}
/* TWO NUMBERS THAT ARE ONE MEASUREMENT — width × height, on one line, under
   one unit. A screen is a rectangle, and a rectangle read down four separate
   rows is a rectangle the reader has to reassemble.

   A cell is either { value, step, onChange, key } and typeable, or { text } and
   not. Both are laid out identically so the columns stay columns; only the fill
   and the border tell them apart, which is the honest signal — a derived number
   that looks like a field invites a click that cannot land.

   `mark` draws a red tick against the label, for rows that belong to a corner
   screen's second panel. A tick rather than a heading: a heading would put the
   second panel in a region of its own, and the whole point is that its pixel
   count sits directly under the first one's, in the same columns, to be
   compared at a glance. */
export function pairRow(label, cells, unit, opts = {}){
  const f = el('div','f f2' + (opts.mark ? ' f2--sub' : ''));
  const l = el('label');
  if(opts.mark) l.appendChild(el('span','mark'));
  l.appendChild(document.createTextNode(label));
  if(opts.title) l.title = opts.title;
  f.appendChild(l);
  const d = el('div','duo');
  cells.forEach((c, i)=>{
    if(i) d.appendChild(el('span','times','\u00d7'));
    if(c.text != null){
      const g = el('span','ghost', c.text);
      if(c.title) g.title = c.title;
      d.appendChild(g);
    }else{
      /* A cell here is half the width numRow's own field gets, so the plain
         flex-sibling stepper (numInput's default) has nowhere to go \u2014 the
         same room problem vec3Row's three-up X/Y/Z solved by overlaying the
         stepper on the input's own right edge instead of beside it (see
         .f3/.mcell in app.css). Same fix here: a small relatively-positioned
         wrapper, the stepper absolutely placed inside it. */
      const cell = el('div','pcell');
      const inp = numCell(c.value, c.step, c.onChange, c.key);
      cell.appendChild(inp);
      addStepper(cell, inp.getFull, v => inp.setValue(+(+v).toFixed(decimalsFor(c.step))), c.step);
      d.appendChild(cell);
    }
  });
  f.appendChild(d);
  if(unit) f.appendChild(el('span','unit', unit));
  return f;
}
export function vec3Row(label, obj, keys, step, onChange, refs){
  const f = el('div','v3');
  f.appendChild(el('label',null,label));
  const g = el('div','f3');
  const dp = step < 1 ? 3 : 1;
  keys.forEach((k,idx)=>{
    // x/y/z — the gizmo's own axis colours (scene/gizmo.js's GZC), so a
    // field here reads against the same red/green/blue as the transform
    // grid it sits beside in spirit (Design's Place tab, ui/rail-right.js).
    const d = el('div', ['x','y','z'][idx]);
    d.dataset.field = label + ' ' + ['X','Y','Z'][idx];
    const ax = el('span','ax',['X','Y','Z'][idx]);
    d.appendChild(ax);
    const i = decimalInput(el('input','inp')); i.step = step; i.value=fmt(obj[k],3);
    const setValue = v=>{ const q = +v.toFixed(dp); obj[k] = q; i.value = q.toFixed(dp); onChange(k); };
    i.addEventListener('input',()=>{ const v=parseFloat(i.value); if(isFinite(v)){ obj[k]=v; onChange(k); } });
    histOn(i);
    d.appendChild(i); g.appendChild(d);
    // NOT makeScrub(ax, ...) — .f3 .ax is pointer-events:none (app.css) so a
    // click there passes straight through to the input beneath it; a scrub
    // handle wired to it would never receive a pointer event. The stepper
    // below is the field's actual drag/click handle now.
    addStepper(d, ()=>obj[k], setValue, step);
    if(refs) refs.push({target:obj, key:k, input:i, d:dp});
  });
  f.appendChild(g);
  return f;
}
/* opts is [value, text] pairs. A pair whose value is null starts an <optgroup>
   with that text as its label — which is how the typeface menu separates
   Display from Sans from the user's own uploads without needing a second
   control. Plain lists are unaffected. */
export function selectRow(label, value, opts, onChange){
  const s = el('select','inp');
  let group = null;
  opts.forEach(([v,t])=>{
    if(v === null){ group = el('optgroup'); group.label = t; s.appendChild(group); return; }
    const o=el('option',null,t); o.value=v; if(String(v)===String(value))o.selected=true;
    (group || s).appendChild(o);
  });
  s.addEventListener('change',()=>onChange(s.value));
  histOn(s);
  return fieldRow(label, s);
}
export function colorRow(label, value, onChange){
  const wrap = el('div','colorwrap');
  const sw = el('input','inp'); sw.type='color'; sw.value=value;
  const hex = el('input','inp'); hex.type='text'; hex.value=value; hex.maxLength=7;
  hex.spellcheck = false; hex.autocomplete = 'off';
  sw.addEventListener('input',()=>{ hex.value = sw.value; onChange(sw.value); });
  hex.addEventListener('input',()=>{
    const v = hex.value.startsWith('#') ? hex.value : '#'+hex.value;
    if(/^#[0-9a-fA-F]{6}$/.test(v)){ sw.value = v; onChange(v); }
  });
  // Leaving it on something that never became valid (or missing the #)
  // snaps back to whatever the swatch actually holds, rather than keeping
  // a value that was never actually applied.
  hex.addEventListener('blur',()=>{ hex.value = sw.value; });
  histOn(sw); histOn(hex);
  wrap.appendChild(sw); wrap.appendChild(hex);
  return fieldRow(label, wrap);
}
export function checkRow(label, value, onChange){
  const l = el('label','chk');
  const i = el('input'); i.type='checkbox'; i.checked=value;
  i.addEventListener('change',()=>onChange(i.checked));
  histOn(i);
  l.appendChild(i); l.appendChild(el('span',null,label));
  return l;
}
export function sliderRow(label, value, min, max, step, onChange){
  const f = el('div','f');
  f.dataset.field = label;
  f.appendChild(el('label',null,label));
  const c = el('div','ctl');
  const r = el('input','rng'); r.type='range'; r.min=min; r.max=max; r.step=step; r.value=value;
  const v = el('span','unit', (+value).toFixed(2));
  /* How much of the track is filled, as a percentage, for the gradient in
     app.css's .rng. A native range gives no way to colour one side of the
     track, and an unfilled one reads as a control that has not been set —
     Firefox has ::-moz-range-progress for exactly this and Chromium does not. */
  const paint = ()=>{
    const lo = +r.min, hi = +r.max;
    const pct = hi > lo ? ((+r.value - lo) / (hi - lo)) * 100 : 0;
    r.style.setProperty('--pct', pct.toFixed(2) + '%');
  };
  paint();
  r.addEventListener('input',()=>{ paint(); v.textContent=(+r.value).toFixed(2); onChange(parseFloat(r.value)); });
  histOn(r);
  c.appendChild(r); c.appendChild(v); f.appendChild(c);
  return f;
}
/* A row of mutually-exclusive buttons standing in for what used to be a
   checkbox — "Fills the screen / Custom", "Colour / Transparent". A tickbox
   reads as one setting among many at the same weight; naming both states
   says what flipping it actually does before you click, not after. */
export function segRow(label, value, options, onChange, opts = {}){
  const f = el('div','f');
  f.dataset.field = opts.key || label;
  if(label) f.appendChild(el('label',null,label));
  const seg = el('span','seg'+(opts.sm?' seg--sm':''));
  seg.setAttribute('role','tablist');
  if(label) seg.setAttribute('aria-label', label);
  options.forEach(([v,t])=>{
    const b = el('button',null,t);
    b.type = 'button';
    b.setAttribute('role','tab');
    b.setAttribute('aria-selected', String(v===value));
    b.onclick = ()=>{
      if(b.getAttribute('aria-selected')==='true') return;
      seg.querySelectorAll('button').forEach(x=>x.setAttribute('aria-selected','false'));
      b.setAttribute('aria-selected','true');
      pushHistory();
      onChange(v);
    };
    seg.appendChild(b);
  });
  f.appendChild(seg);
  return f;
}
/* The reasoning behind a row, one tap away instead of always open. Returns
   the small circular "i" button (drop it beside the row it explains) and the
   paragraph it reveals (append that right after). At rest the panel reads as
   controls; the explanation is still there, complete, for whoever wants it. */
export function infoToggle(text){
  const btn = el('button','info','i');
  btn.type = 'button';
  btn.title = 'Why';
  btn.setAttribute('aria-expanded','false');
  const note = el('p','why note',text);
  btn.onclick = ()=>{
    const on = note.classList.toggle('on');
    btn.setAttribute('aria-expanded', String(on));
  };
  return { btn, note };
}
/* THE PLACE-TAB GRID — position, rotation and scale as one x/y/z table
   instead of three rows each re-stating its own axis letters. `rows` is
   [{label, obj, keys:['x','y','z'], step, unit, dp, onChange}]; every row
   shares the same three columns, so the axis header is written once. Each
   cell keeps the field's usual scrub stepper (drag to change) — the mockup
   this is built from is unwired HTML, and dropping that here would be a
   real loss, not a simplification.

   Each row is wrapped in a display:contents div rather than appended as
   bare cells — invisible to the grid layout (its children still land in
   the shared columns exactly as if they were direct children of .mtx), but
   a single real node stopwatch() can find a <label> inside and wrap, so
   Position/Rotation/Scale keep their own independent keyframe toggle
   despite reading as one table. Left on `r.rowEl` for the caller to do
   that wrapping (rail-right.js imports stopwatch from ui/timeline-view.js;
   this module can't, timeline-view.js already imports FROM here). */
export function transformGrid(rows, refs){
  const AX = ['x','y','z'];
  const wrap = el('div','mtx');
  wrap.appendChild(el('span'));
  AX.forEach(a=>wrap.appendChild(el('span','axis '+a, a)));
  wrap.appendChild(el('span'));
  rows.forEach(r=>{
    const dp = r.dp != null ? r.dp : (r.step < 1 ? 3 : 1);
    const rowEl = el('div','mrow');
    rowEl.appendChild(el('label',null,r.label));
    r.keys.forEach((k,idx)=>{
      const cell = el('div','mcell '+AX[idx]);
      cell.dataset.field = r.label + ' ' + AX[idx].toUpperCase();
      const i = decimalInput(el('input','inp')); i.step = r.step; i.value = fmt(r.obj[k],3);
      const setValue = v=>{ const q = +v.toFixed(dp); r.obj[k] = q; i.value = q.toFixed(dp); r.onChange(k); };
      i.addEventListener('input',()=>{ const v=parseFloat(i.value); if(isFinite(v)){ r.obj[k]=v; r.onChange(k); } });
      histOn(i);
      cell.appendChild(i);
      addStepper(cell, ()=>r.obj[k], setValue, r.step);
      if(refs) refs.push({target:r.obj, key:k, input:i, d:dp});
      rowEl.appendChild(cell);
    });
    rowEl.appendChild(el('span','u', r.unit||''));
    wrap.appendChild(rowEl);
    r.rowEl = rowEl;
  });
  return wrap;
}
/* PLACE / FORM / LOOK — the three questions the inspector answers, replacing
   the old unlabelled icon strip that duplicated the panel headers directly
   beneath it. `panes` is [[title, container], ...]; only one shows at a
   time, remembered per title the same way panel()'s own open/closed state
   is, so switching object then back doesn't reset which question you were
   looking at. */
const tabState = new Map();
export function tabStrip(panes, opts = {}){
  const key = opts.key || panes.map(([t])=>t).join('/');
  const saved = tabState.get(key);
  let active = panes.some(([t])=>t===saved) ? saved : panes[0][0];
  const bar = el('div','seg tabstrip');
  bar.setAttribute('role','tablist');
  const sync = ()=>{
    /* classList, not the native `hidden` property — `.panel__bd` (and
       `.v3`, the pivot widget's own wrapper) sets `display:flex` as an
       ordinary author rule, and an author rule beats the user-agent
       stylesheet's `[hidden]{display:none}` at equal specificity
       regardless of which one is more specific. `.hidden` here carries
       `!important` for exactly this reason — see app.css. */
    panes.forEach(([title, node])=>{ node.classList.toggle('hidden', title !== active); });
    bar.querySelectorAll('button').forEach(b=>b.setAttribute('aria-selected', String(b.dataset.t===active)));
  };
  panes.forEach(([title])=>{
    const b = el('button',null,title);
    b.type = 'button';
    b.dataset.t = title;
    b.setAttribute('role','tab');
    b.onclick = ()=>{ active = title; tabState.set(key, active); sync(); };
    bar.appendChild(b);
  });
  sync();
  return bar;
}
export function readout(rows){
  const d = el('div','ro');
  rows.forEach(([k,v,cls])=>{
    const r = el('div');
    r.appendChild(el('dt',null,k));
    r.appendChild(el('dd',cls||null,v));
    d.appendChild(r);
  });
  return d;
}
export const ICONS = {
  model:'<path d="M8 1.6 14 5v6l-6 3.4L2 11V5z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M2 5l6 3.4L14 5M8 8.4V14.6" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>',
  camera:'<rect x="1.6" y="5" width="10.6" height="7.4" rx="1.4" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M12.2 8.2 15.6 6v6l-3.4-2.2z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M4.4 5V3.6h3.4V5" fill="none" stroke="currentColor" stroke-width="1.1"/>',
  light:'<circle cx="8.5" cy="8" r="3.2" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8.5 1v2.1M8.5 12.9V15M1.6 8h2.1M13.3 8h2.1M3.6 3.1 5.1 4.6M11.9 11.4l1.5 1.5M13.4 3.1 11.9 4.6M5.1 11.4 3.6 12.9" stroke="currentColor" stroke-width="1.1"/>',
  recess:'<rect x="1.7" y="2.4" width="13.6" height="11.2" fill="none" stroke="currentColor" stroke-width="1.2"/><rect x="5.1" y="5.5" width="6.8" height="5" fill="none" stroke="currentColor" stroke-width="1"/><path d="M1.7 2.4 5.1 5.5M15.3 2.4 11.9 5.5M1.7 13.6 5.1 10.5M15.3 13.6 11.9 10.5" stroke="currentColor" stroke-width=".85"/>',
  frame:'<path d="M1.7 2.4h13.6v11.2H1.7z M5.1 5.5h6.8v5H5.1z" fill="currentColor" fill-rule="evenodd" stroke="currentColor" stroke-width="1"/>',
  text:'<path d="M3 4h11M8.5 4v9" fill="none" stroke="currentColor" stroke-width="1.4"/>',
  textarea:'<rect x="1.8" y="2.4" width="12.4" height="11.2" rx="1" fill="none" stroke="currentColor" stroke-width="1.1"/><path d="M4 5.7h8.4M4 8h8.4M4 10.3h5.4" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>',
  image:'<rect x="2" y="3.5" width="13" height="9" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M2 10l3.4-3 3 2.6L11 7l4 3.6" fill="none" stroke="currentColor" stroke-width="1.2"/>',
  video:'<rect x="1.5" y="3.5" width="9.5" height="9" rx="1.2" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M11 6.3 15 4v8l-4-2.3z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>'
};
/* A null: a square with a cross through its centre — After Effects draws its
   null the same way, and it reads as "a point you can hold", not a thing. */
ICONS.null = '<rect x="3" y="3" width="10" height="10" fill="none" stroke="currentColor" stroke-width="1.2" stroke-dasharray="2.2 1.6"/><path d="M8 5.2v5.6M5.2 8h5.6" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>';
/* A shape is added from the 3D Model button, so it wears that button's cube:
   the inspector header then shows the tool it came from. */
ICONS.shape = ICONS.model;

/* One drawing per basic shape, for the Add 3D Model dialog — same 16-unit line
   style as ICONS so the tiles read as part of the same toolset. */
const SW = 'fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"';
export const SHAPE_ICONS = {
  box:      `<path ${SW} d="M8 1.8 13.8 4.8v6.4L8 14.2 2.2 11.2V4.8z"/><path ${SW} d="M2.2 4.8 8 7.8l5.8-3M8 7.8v6.4"/>`,
  sphere:   `<circle ${SW} cx="8" cy="8" r="6"/><ellipse ${SW} cx="8" cy="8" rx="6" ry="2.1"/>`,
  cylinder: `<ellipse ${SW} cx="8" cy="3.6" rx="4.8" ry="1.7"/><path ${SW} d="M3.2 3.6v8.8M12.8 3.6v8.8"/><path ${SW} d="M3.2 12.4c0 .95 2.15 1.7 4.8 1.7s4.8-.75 4.8-1.7"/>`,
  cone:     `<path ${SW} d="M8 1.8 3.2 12.4M8 1.8l4.8 10.6"/><ellipse ${SW} cx="8" cy="12.4" rx="4.8" ry="1.7"/>`,
  pyramid:  `<path ${SW} d="M8 1.8 2.4 11.6 7.2 14.2l6.4-3.4z"/><path ${SW} d="M8 1.8l-.8 12.4"/>`,
  torus:    `<ellipse ${SW} cx="8" cy="8" rx="6.2" ry="3.6"/><ellipse ${SW} cx="8" cy="7.6" rx="2.6" ry="1.2"/>`,
  capsule:  `<rect ${SW} x="4.6" y="1.8" width="6.8" height="12.4" rx="3.4"/><path ${SW} d="M4.6 8c0 .9 1.5 1.5 3.4 1.5s3.4-.6 3.4-1.5"/>`,
  plane:    `<path ${SW} d="M2 10.6 6.4 5h7.6L9.6 10.6z"/>`
};
export const EYE_ON  = '<svg width="13" height="13" viewBox="0 0 16 16"><path d="M8 3.5C4.4 3.5 1.8 6.4 1 8c.8 1.6 3.4 4.5 7 4.5s6.2-2.9 7-4.5c-.8-1.6-3.4-4.5-7-4.5z" fill="none" stroke="currentColor" stroke-width="1.1"/><circle cx="8" cy="8" r="1.9" fill="currentColor"/></svg>';
export const EYE_OFF = '<svg width="13" height="13" viewBox="0 0 16 16"><path d="M8 3.5C4.4 3.5 1.8 6.4 1 8c.8 1.6 3.4 4.5 7 4.5s6.2-2.9 7-4.5c-.8-1.6-3.4-4.5-7-4.5z" fill="none" stroke="currentColor" stroke-width="1.1"/><path d="M3 13 13 3" stroke="currentColor" stroke-width="1.3"/></svg>';

/* Mid-scrub the rails are not rebuilt — the DOM churn makes the drag stutter —
   so the request is remembered and flushed on release. Same read-only-binding
   reason as setTexTarget: rails.js used to set these directly. */
export const deferRail = side => { if(side === 'L') railLPending = true; else railRPending = true; };

/* Panels are rebuilt wholesale on every change (see rail-right.js's own note on
   this), and several fields — Cap height, Extrusion, a recess's auto-fit
   dimensions, now Text Area's Width/Base size/Padding — chain a rebuild INTO
   their own onChange so some other readout in the same panel stays current.
   Typing into one of those fields tore the DOM out from under itself on every
   keystroke: the input got destroyed and rebuilt, and focus fell back to
   <body> — indistinguishable from the field just refusing your next character.

   Restoring focus alone is not enough. A fresh numInput() shows
   String(round(P.capH)) — the last value onChange actually COMMITTED — not the
   raw text sitting in the old input. Mid-typing that raw text is ahead of the
   model on purpose: "2." parses to the finite number 2 (so onChange runs and
   P.capH becomes 2) but the trailing dot you just typed never reaches the
   model at all. Restore the raw string too, or every rebuild quietly eats
   whatever you'd typed that the model hadn't caught up to yet — a decimal
   point, a leading "-", the digit after it. numRow/vec3Row/sliderRow/
   fieldRow/stackRow all tag their row with data-field=label for exactly this:
   capture which field (and where in it, and what it literally said) had focus
   before the rebuild, restore all of that to the same field by that label
   after. */
export function captureFocus(container){
  const a = document.activeElement;
  if(!a || !container.contains(a)) return null;
  if(a.tagName !== 'INPUT' && a.tagName !== 'SELECT' && a.tagName !== 'TEXTAREA' && !a.isContentEditable) return null;
  const row = a.closest('[data-field]');
  if(!row) return null;
  return { field: row.dataset.field, value: 'value' in a ? a.value : null,
           start: a.selectionStart, end: a.selectionEnd };
}
const FOCUSABLE = 'input,select,textarea,[contenteditable]';
export function restoreFocus(container, f){
  if(!f) return;
  const row = container.querySelector(`[data-field="${CSS.escape(f.field)}"]`);
  /* numCell puts data-field on the input ITSELF, so the thing found here is
     sometimes the field rather than a row containing one. Asking a bare input
     for a descendant input returns nothing, and focus was silently dropped. */
  const input = !row ? null
    : row.matches(FOCUSABLE) ? row : row.querySelector(FOCUSABLE);
  if(!input) return;
  input.focus();
  /* AFTER focus(), not before — a fresh numInput's own 'focus' listener sets
     .value = String(full) as part of dispatching that event, and this has to
     win over it or the raw text restore below is immediately undone. */
  if(f.value != null && 'value' in input && input.tagName !== 'SELECT') input.value = f.value;
  if(f.start != null && input.setSelectionRange){ try{ input.setSelectionRange(f.start, f.end); }catch{} }
}

